import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Box, Typography, Tabs, Tab, CircularProgress, Alert } from '@mui/material';
import { useFund } from '@lingaai/shared-hooks';
import { SubmissionsTab } from './fund-details/SubmissionsTab';
import { PortfolioCompaniesTab } from './fund-details/PortfolioCompaniesTab';
import { MetricDefinitionsTab } from './fund-details/MetricDefinitionsTab';
import { FundSettingsTab } from './fund-details/FundSettingsTab';

export function FundDetails() {
  const { fundId } = useParams<{ fundId: string }>();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(0);
  const { data: fund, isLoading, error } = useFund(fundId!);

  // If coming from global search with params, switch to Portfolio Companies tab
  useEffect(() => {
    const hasSearchParams = searchParams.get('search') || searchParams.get('tab');
    if (hasSearchParams) {
      setActiveTab(1); // Portfolio Companies tab
    }
  }, [searchParams]);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !fund) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error instanceof Error ? error.message : 'Fund not found'}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 0.5 }}>
        {fund.name}
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {fund.reporting_frequency && fund.reporting_day_of_month ? (
          <>
            Portfolio companies report <strong>{fund.reporting_frequency}</strong> on the{' '}
            <strong>
              {fund.reporting_day_of_month}
              {fund.reporting_day_of_month === 1 ? 'st' : fund.reporting_day_of_month === 2 ? 'nd' : fund.reporting_day_of_month === 3 ? 'rd' : 'th'}
            </strong>
            {fund.reporting_timezone && ` (${fund.reporting_timezone})`}
          </>
        ) : (
          'Reporting schedule not configured'
        )}
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
          <Tab label="Submissions" />
          <Tab label="Portfolio Companies" />
          <Tab label="Metric Definitions" />
          <Tab label="Settings" />
        </Tabs>
      </Box>

      {activeTab === 0 && <SubmissionsTab fund={fund} />}
      {activeTab === 1 && <PortfolioCompaniesTab fundId={fund.id} />}
      {activeTab === 2 && <MetricDefinitionsTab fundId={fund.id} />}
      {activeTab === 3 && <FundSettingsTab fund={fund} />}
    </Box>
  );
}
